import * as React from "react";
import {
  Box,
  Container,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import XIcon from "@mui/icons-material/X";
import YouTubeIcon from "@mui/icons-material/YouTube";

const SEEDS_LOGO_SRC = "/images/seedsLogo.png";
const PRIMA_LOGO_SRC = "/images/prima-eu.png";
const CERTH_LOGO_SRC = "/images/certh-logo.png";
const ITI_LOGO_SRC = "/images/iti_logo_en.png";

const socialLinkSx = {
  width: 40,
  height: 40,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  color: "text.secondary",

  "&:hover": {
    color: "#f0a800",
    bgcolor: "rgba(240, 168, 0, 0.08)",
    borders: "none",
  },
};

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        mt: "auto",
        bgcolor: "#fff",
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              lg: "1.35fr 0.65fr 1fr 1fr",
            },
            gap: { xs: 4, md: 5, lg: 6 },
            alignItems: "center",
          }}
        >
          <Box sx={{ justifySelf: { xs: "start", sm: "stretch" } }}>
            <Stack direction="row" spacing={1.25} alignItems="center" mb={1.5}>
              <Box
                component="img"
                src={SEEDS_LOGO_SRC}
                alt="SEEDS"
                sx={{ width: 60, height: 60, objectFit: "contain" }}
              />
              <Typography sx={{ fontSize: "0.94rem", fontWeight: 700 }}>
                SEEDS Platform
              </Typography>
            </Stack>
            <Typography
              color="text.secondary"
              sx={{
                display: "block",
                maxWidth: 410,
                fontSize: "0.8rem",
                lineHeight: 1.55,
              }}
            >
              The SEEDS platform is part of the PRIMA Programme supported by the
              European Union under Grant Agreement number 2332 — Call 2023
              Section 1 AGROFOOD RIA.
            </Typography>
          </Box>

          <Box
            sx={{
              justifySelf: { xs: "start", sm: "end", lg: "center" },
              textAlign: { xs: "left", sm: "right", lg: "center" },
            }}
          >
            <Typography sx={{ fontSize: "0.94rem", fontWeight: 700, mb: 1.5 }}>
              Follow Us
            </Typography>
            <Stack
              direction="row"
              spacing={0.75}
              justifyContent={{ xs: "flex-start", sm: "flex-end", lg: "center" }}
            >
              <IconButton
                component="a"
                href="https://www.linkedin.com/company/seeds-eu-project/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                sx={socialLinkSx}
              >
                <LinkedInIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                component="a"
                href="https://twitter.com/SeedsEu"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter / X"
                sx={socialLinkSx}
              >
                <XIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                component="a"
                href="https://www.youtube.com/channel/UCZgUzDhqDep8scSxUhuFQuA"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                sx={socialLinkSx}
              >
                <YouTubeIcon sx={{ fontSize: 21 }} />
              </IconButton>
            </Stack>
          </Box>

          <Box
            sx={{
              justifySelf: { xs: "start", sm: "start", lg: "end" },
              width: "100%",
              maxWidth: 360,
            }}
          >
            <Box
              component="img"
              src={PRIMA_LOGO_SRC}
              alt="PRIMA Programme and European Union"
              sx={{
                width: { xs: "100%", sm: 300 },
                maxWidth: 360,
                height: "auto",
                display: "block",
                filter:
                  "drop-shadow(0 0 1px rgba(0,0,0,.9)) drop-shadow(0 1px 1px rgba(0,0,0,.6))",
              }}
            />
          </Box>

          <Box
            sx={{
              justifySelf: { xs: "start", sm: "end" },
              width: { xs: 180, sm: 300 },
            }}
          >
            <Stack spacing={2}>
              <Box
                component="a"
                href="https://www.certh.gr/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Centre for Research and Technology Hellas"
                sx={{ display: "block", lineHeight: 0 }}
              >
                <Box
                  component="img"
                  src={CERTH_LOGO_SRC}
                  alt="Centre for Research and Technology Hellas"
                  sx={{
                    width: "70%",
                    height: "auto",
                    display: "block",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Box
                component="a"
                href="https://www.iti.gr/iti/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Information Technologies Institute"
                sx={{ display: "block", lineHeight: 0 }}
              >
                <Box
                  component="img"
                  src={ITI_LOGO_SRC}
                  alt="Information Technologies Institute"
                  sx={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Stack>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Typography
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", fontSize: "0.8rem" }}
        >
          © {year}{" "}
          <Link
            href="https://seeds-prima.eu/"
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            color="inherit"
            sx={{ textDecoration: "none", "&:hover": { textDecoration: "none" } }}
          >
            SEEDS PLATFORM
          </Link>
          . All rights reserved.
        </Typography>

        <Stack
          direction="row"
          spacing={0.75}
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 1, flexWrap: "wrap" }}
        >
          <Link
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            color="text.secondary"
            sx={{ fontSize: "0.8rem", textDecoration: "none", "&:hover": { textDecoration: "none" } }}
          >
            Privacy Policy
          </Link>
          <Typography color="text.secondary" sx={{ fontSize: "0.8rem" }}>
            |
          </Typography>
          <Link
            href="/terms-of-use"
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            color="text.secondary"
            sx={{ fontSize: "0.8rem", textDecoration: "none", "&:hover": { textDecoration: "none" } }}
          >
            Terms of Use
          </Link>
        </Stack>
      </Container>
    </Box>
  );
}
